// Propósito: helpers DOM puros del sidebar.
import { slideUp } from '@composables/slideUp.js';
import { slideToggle } from '@composables/slideToggle.js';

// Detecta modo móvil por ancho.
export function isMobileWidth(width: number): boolean {
  return width <= 767;
}

// Lee el tiempo de expansión.
export function expandTimeOf(sidebar: Element | null, fallback: number): number {
  if (hasNoAnimation(sidebar)) return 0;
  return fallback;
}

// Indica si el sidebar desactiva la animación.
export function hasNoAnimation(sidebar: Element | null): boolean {
  return Boolean(sidebar && sidebar.getAttribute('data-disable-slide-animation'));
}

// Busca el sidebar principal.
export function findSidebar(): HTMLElement | null {
  return document.querySelector('.app-sidebar:not(.app-sidebar-end)');
}

// Busca el perfil del sidebar.
export function findProfile(): HTMLElement | null {
  return document.querySelector('#appSidebarProfileMenu');
}

// Sincroniza el estado del menú perfil.
export function syncProfileMenu(menu: Element | null, profile: HTMLElement) {
  if (isShown(profile)) hideMenu(menu);
  else showMenu(menu);
}

// Indica si el perfil está visible.
export function isShown(profile: HTMLElement): boolean {
  return profile.style.display == 'block';
}

// Marca el menú como oculto.
export function hideMenu(menu: Element | null) {
  menu?.classList.remove('active');
}

// Marca el menú como visible.
export function showMenu(menu: Element | null) {
  menu?.classList.add('active');
}

// Aplica el toggle al perfil.
export function toggleProfile(profile: HTMLElement, time: number) {
  slideToggle(profile, time);
  profile.classList.toggle('expand');
}

// Colapsa un item distinto del objetivo.
export function collapseOne(menu: Element, target: Element | null, time: number) {
  const other = menu.nextElementSibling;
  if (other !== target) hideOther(other, time);
}

// Oculta otro submenu.
export function hideOther(other: Element | null, time: number) {
  slideUp(otherTarget(other), time);
  otherItem(other)?.classList.remove('expand');
  otherItem(other)?.classList.add('closed');
}

// Normaliza el objetivo para slideUp.
export function otherTarget(other: Element | null): any {
  return other;
}

// Busca el item contenedor.
export function otherItem(other: Element | null): Element | null {
  return other?.closest('.menu-item') ?? null;
}

// Alterna el objetivo actual.
export function toggleTarget(target: Element, time: number) {
  const item = target.closest('.menu-item');
  if (isExpanded(item, target)) collapseTarget(item, target, time);
  else expandTarget(item, target, time);
}

// Indica si el item está expandido o activo oculto.
export function isExpanded(item: Element | null, target: Element): boolean {
  return hasExpand(item) || isActiveHidden(item, target);
}

// Indica si tiene clase expand.
export function hasExpand(item: Element | null): boolean {
  return Boolean(item?.classList.contains('expand'));
}

// Indica si está activo pero oculto.
export function isActiveHidden(item: Element | null, target: Element): boolean {
  return hasActive(item) && isHidden(target);
}

// Indica si tiene clase active.
export function hasActive(item: Element | null): boolean {
  return Boolean(item?.classList.contains('active'));
}

// Indica si el objetivo está oculto.
export function isHidden(target: Element): boolean {
  return !(target as HTMLElement).style.display;
}

// Colapsa el objetivo.
export function collapseTarget(item: Element | null, target: Element, time: number) {
  item?.classList.remove('expand');
  item?.classList.add('closed');
  slideToggle(target, time);
}

// Expande el objetivo.
export function expandTarget(item: Element | null, target: Element, time: number) {
  item?.classList.add('expand');
  item?.classList.remove('closed');
  slideToggle(target, time);
}

// Colapsa los demás menús.
export function collapseOthers(menus: Element[], target: Element | null, time: number) {
  for (const menu of menus) collapseOne(menu, target, time)
}

// Activa el click de un menú.
export function bindMenuClick(menu: any, menus: Element[], time: number) {
  menu.onclick = function (this: Element, e: Event) {
    handleMenuClick.call(this, e, menus, time);
  };
}

// Maneja el click usando el elemento como contexto.
export function handleMenuClick(this: Element, e: Event, menus: Element[], time: number) {
  e.preventDefault();
  const target = this.nextElementSibling;
  if (!target) return;
  collapseOthers(menus, target, time);
  toggleTarget(target, time);
}

// Lee el scroll guardado.
export function savedScroll(): string | null {
  return localStorage["sidebarScroll"];
}

// Indica si hay soporte de storage.
export function hasStorage(): boolean {
  return typeof (Storage) !== 'undefined';
}

// Calcula el siguiente estado activo.
export function nextActive(state: string): string {
  if (isCollapsed(state)) return 'expand';
  return 'collapse';
}

// Indica si el estado es colapsado.
export function isCollapsed(state: string): boolean {
  return Boolean(state && state == 'collapse');
}

// Calcula el siguiente estado inactivo.
export function nextInactive(state: string): string {
  if (isExpandedState(state)) return 'collapse';
  return 'expand';
}

// Indica si el estado es expandido.
export function isExpandedState(state: string): boolean {
  return Boolean(state && state == 'expand');
}
